import type { DefaultRootState } from "react-redux";
import type { Dispatch } from "redux";
import type { ThunkAction } from "redux-thunk";
import {
  type ICreateUserAttributes,
  type IUser,
  Inventory,
} from "interfaces/users/inventory";

import { RequestStatus } from "types/request";

const { postCreateUser, postUserRoles, getUserRoles } = Inventory;

export type InventoryUserActionTypes =
  | ISetInventoryRequestStatusAction
  | IReceiveUserInventoryRolesAction
  | ICreateUserAction;

type ThunkResult<R> = ThunkAction<
  R,
  DefaultRootState,
  undefined,
  InventoryUserActionTypes
>;

export enum TypeKeys {
  SET_INVENTORY_REQUEST_STATUS_ACTION = "USERS/INVENTORY/SET_INVENTORY_REQUEST_STATUS_ACTION",
  CREATE_USER_ACTION = "USERS/INVENTORY/CREATE_INVENTORY_USER_ACTION",
  RECEIVE_USER_INV_ROLES = "USERS/INVENTORY/RECEIVE_USER_INV_ROLES",
}

export enum InventoryRequestKey {
  CreateUser = "CreateUser",
  UpdateUserRoles = "UpdateUserRoles",
}

export interface ISetInventoryRequestStatusAction {
  type: TypeKeys.SET_INVENTORY_REQUEST_STATUS_ACTION;
  requestKey: InventoryRequestKey;
  requestStatus: RequestStatus;
}

export interface ICreateUserAction {
  type: TypeKeys.CREATE_USER_ACTION;
  user: IUser;
}
const setInventoryRequestStatus = (
  requestKey: InventoryRequestKey,
  requestStatus: RequestStatus
): ISetInventoryRequestStatusAction => {
  return {
    type: TypeKeys.SET_INVENTORY_REQUEST_STATUS_ACTION,
    requestKey,
    requestStatus,
  };
};

export const createInventoryUser = (
  attrs: ICreateUserAttributes,
  settingsToken: string
): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setInventoryRequestStatus(
        InventoryRequestKey.CreateUser,
        RequestStatus.REQUESTING
      )
    );

    return postCreateUser(attrs, settingsToken).then((user) => {
      dispatch(
        setInventoryRequestStatus(
          InventoryRequestKey.CreateUser,
          RequestStatus.SUCCESS
        )
      );

      dispatch({
        type: TypeKeys.CREATE_USER_ACTION,
        user,
      });
    });
  };
};

export function saveInventoryUserRoles(
  userId: string,
  roles: string[],
  token: string
): ThunkResult<void> {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setInventoryRequestStatus(
        InventoryRequestKey.UpdateUserRoles,
        RequestStatus.REQUESTING
      )
    );
    return postUserRoles(userId, roles, token)
      .then((updatedRoles) => {
        dispatch(
          setInventoryRequestStatus(
            InventoryRequestKey.UpdateUserRoles,
            RequestStatus.SUCCESS
          )
        );
        dispatch(receiveUserInventoryRoles(userId, updatedRoles.items));
      })
      .catch(() => {
        dispatch(
          setInventoryRequestStatus(
            InventoryRequestKey.UpdateUserRoles,
            RequestStatus.FAILURE
          )
        );
      });
  };
}

export function resetUserRoleRequestStatus(): ThunkResult<void> {
  return (dispatch) =>
    dispatch(
      setInventoryRequestStatus(
        InventoryRequestKey.UpdateUserRoles,
        RequestStatus.NOT_STARTED
      )
    );
}

export interface IReceiveUserInventoryRolesAction {
  type: TypeKeys.RECEIVE_USER_INV_ROLES;
  userId: string;
  roles: string[];
}

function receiveUserInventoryRoles(
  userId: string,
  roles: string[]
): IReceiveUserInventoryRolesAction {
  return {
    type: TypeKeys.RECEIVE_USER_INV_ROLES,
    userId,
    roles,
  };
}

export function fetchUserInventoryRoles(
  userId: string,
  token: string
): ThunkResult<void> {
  return (dispatch: Dispatch<any>): Promise<void> => {
    return getUserRoles(userId, token).then((response) => {
      dispatch(receiveUserInventoryRoles(userId, response.items));
    });
  };
}
